<script>
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import CourseFilter from "@/components/CustomComponent/modal/course-filter.vue";
import Swal from "sweetalert2";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import { useCourseDataStore } from "@/stores/course/course-data-store";
import { useEnrollmentStore } from "@/stores/access/enrollment-store";
import { useUserStore } from "@/stores/user/user-store";

export default {
  components: {
    BaseLoading,
    CourseFilter,
  },
  data() {
    return {
      sharedStore: useSharedStore(),
      authStore: useAuthStore(),
      courseFetchStore: useCourseFetchStore(),
      courseDataStore: useCourseDataStore(),
      enrollmentStore: useEnrollmentStore(),
      userStore: useUserStore(),
      currentPage: 1,
      itemsPerPage: 5,
    };
  },

  computed: {
    myCourses() {
      const instructorId = Number(this.authStore.currentUser?.id);
      return this.courseFetchStore.filteredAllCourses.filter(
        (course) => Number(course.instructor) === instructorId
      );
    },
    activeCourseCount() {
      return this.myCourses.filter(
        (course) =>
          course.status === "Active" || course.status === "Need Approval Edit"
      ).length;
    },
    disabledCourseCount() {
      return this.myCourses.filter((course) => course.status === "Disabled")
        .length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.myCourses.length / this.itemsPerPage));
    },
    paginatedCourses() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.myCourses.slice(start, end);
    },
  },
  watch: {
    "courseFetchStore.searchKeyword"() {
      this.currentPage = 1;
    },
    "courseFetchStore.filterGroup"() {
      this.currentPage = 1;
    },
    "courseFetchStore.filterDifficulties"() {
      this.currentPage = 1;
    },
    "courseFetchStore.filterStatus"() {
      this.currentPage = 1;
    },
    myCourses() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  mounted() {
    this.courseFetchStore.clearAllFilters();
    this.courseFetchStore.searchKeyword = "";
    this.courseFetchStore.fetchCourseByCreatorList();
    this.enrollmentStore.fetchEnrollmentList();
  },
  methods: {
    async deleteCourse(course) {
      if (this.sharedStore.loading) return;

      const result = await Swal.fire({
        title: "Hapus Course?",
        text: "Aksi ini tidak dapat dibatalkan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;

      await this.courseDataStore.deleteCourse(course);
    },
    editCourse(courseID) {
      this.$router.push({ name: "CourseEdit", params: { courseID } });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    onSearch(event) {
      this.courseFetchStore.setSearchKeyword(event.target.value);
      this.currentPage = 1;
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Courses</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Courses
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <div class="content">
    <div class="container">
      <instructor-header></instructor-header>
      <div class="row">
        <!-- Sidebar -->
        <instructor-sidebar></instructor-sidebar>
        <!-- /Sidebar -->
        <div class="col-lg-9">
          <div class="row">
            <div class="col-xxl col-lg-4 col-md-6">
              <div class="card bg-success">
                <div class="card-body">
                  <h6 class="fw-medium mb-1 text-white">Active Courses</h6>
                  <h4 class="fw-bold text-white">{{ activeCourseCount }}</h4>
                </div>
              </div>
            </div>
            <div class="col-xxl col-lg-4 col-md-6">
              <div class="card bg-secondary">
                <div class="card-body">
                  <h6 class="fw-medium mb-1 text-white">Disabled Courses</h6>
                  <h4 class="fw-bold text-white">{{ disabledCourseCount }}</h4>
                </div>
              </div>
            </div>
          </div>
          <div class="page-title d-flex align-items-center justify-content-between">
            <h5 class="fw-bold">Courses</h5>
          </div>
          <div class="row justify-content-between">
            <div class="col-md-2">
              <div class="mb-3">
                <button
                  type="button"
                  class="btn btn-outline-secondary w-100 d-inline-flex align-items-center"
                  data-bs-toggle="modal"
                  data-bs-target="#filterCourseModal"
                >
                  <i class="feather-filter me-2"></i> Filter
                </button>
              </div>
            </div>
            <div class="col-md-4">
              <div class="input-icon mb-3">
                <span class="input-icon-addon">
                  <i class="isax isax-search-normal-14"></i>
                </span>
                <input
                  type="text"
                  class="form-control form-control-md"
                  placeholder="Search"
                  :value="courseFetchStore.searchKeyword"
                  @input="onSearch"
                />
              </div>
            </div>
          </div>
          <div class="table-responsive custom-table">
            <table class="table">
              <thead class="thead-light">
                <tr>
                  <th>Course</th>
                  <th>Students</th>
                  <th>In progress</th>
                  <th>Completed</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="paginatedCourses.length === 0">
                  <td colspan="6" class="text-center">No data</td>
                </tr>
                <tr
                  v-for="(item, index) in paginatedCourses"
                  :key="item.course_id || index"
                >
                  <td>
                    <div class="d-flex align-items-center">
                      <router-link :to="{
                        name: 'CourseDetail',
                        params: { course_id: item.course_id },
                      }" class="avatar avatar-lg me-2 flex-shrink-0"><img class="img-fluid object-fit-cover"
                          :src="`${authStore.Url}/media/${item.thumbnail}`" alt="" /></router-link>

                      <div>
                        <h6 class="fw-medium mb-2">
                          <router-link :to="{
                            name: 'CourseDetail',
                            params: { course_id: item.course_id },
                          }">{{ item.title }}</router-link>
                        </h6>
                        <div class="d-flex align-items-center">
                          <span
                            v-if="item.level"
                            class="d-inline-flex fs-12 align-items-center me-2 pe-2 border-end"
                            ><i
                              class="isax isax-video-circle me-1 text-gray-9 fw-bold"
                            ></i
                            >{{ item.level }}</span
                          >
                          <span
                            v-else
                            class="d-inline-flex fs-12 align-items-center me-2 pe-2 border-end"
                            ><i
                              class="isax isax-video-circle me-1 text-gray-9 fw-bold"
                            ></i
                            >-</span
                          >
                          <span class="d-inline-flex fs-12 align-items-center"
                            ><i
                              class="isax isax-user me-1 text-gray-9 fw-bold"
                            ></i
                            >{{ authStore.userName(item.instructor) }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{{ item.student_enrolled_count }}</td>
                  <td>
                    {{
                      enrollmentStore.getTotalInProgressByCourse(item.course_id)
                    }}
                  </td>
                  <td>
                    {{
                      enrollmentStore.getTotalCompletedByCourse(item.course_id)
                    }}
                  </td>
                  <td>
                    <span v-if="item.status === 'Active'"
                      class="badge badge-sm bg-success d-inline-flex align-items-center me-1"><i
                        class="fa-solid fa-circle fs-5 me-1"></i>{{ item.status }}</span>
                    <span v-else-if="item.status === 'New'"
                      class="badge badge-sm bg-warning d-inline-flex align-items-center me-1"><i
                        class="fa-solid fa-circle fs-5 me-1"></i>{{ item.status }}</span>
                    <span v-else-if="item.status === 'Disabled'"
                      class="badge badge-sm bg-danger d-inline-flex align-items-center me-1"><i
                        class="fa-solid fa-circle fs-5 me-1"></i>{{ item.status }}</span>
                    <span v-else
                      class="badge badge-sm bg-info d-inline-flex align-items-center me-1"><i
                        class="fa-solid fa-circle fs-5 me-1"></i>{{ item.status }}</span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center">
                      <a
                        v-if="item.status !== 'Disabled'"
                        href="#"
                        class="d-inline-flex fs-14 me-1 action-icon"
                        title="Edit Course"
                        @click.prevent="editCourse(item.course_id)"
                        ><i class="isax isax-edit-2"></i
                      ></a>
                      <a
                        v-if="item.status === 'New'"
                        href="#"
                        class="d-inline-flex fs-14 action-icon"
                        title="Hapus Course"
                        :class="{ disabled: sharedStore.loading }"
                        @click.prevent="deleteCourse(item)"
                        ><i class="isax isax-trash"></i
                      ></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- /pagination -->
          <div class="row align-items-center mt-4">
            <div class="col-md-2">
              <p class="fs-14 fw-500 text-center text-md-start">
                Page {{ currentPage }} of {{ totalPages }}
              </p>
            </div>
            <div class="col-md-10">
              <ul class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0">
                <li class="page-item prev" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="javascript:void(0)" tabindex="-1" @click="goToPage(currentPage - 1)"><i
                      class="fas fa-angle-left"></i></a>
                </li>
                <li class="page-item" :class="{ active: currentPage === page }" v-for="page in totalPages" :key="page">
                  <a class="page-link" href="javascript:void(0)" @click="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item next" :class="{ disabled: currentPage === totalPages || totalPages === 0 }">
                  <a class="page-link" href="javascript:void(0)" @click="goToPage(currentPage + 1)"><i
                      class="fas fa-angle-right"></i></a>
                </li>
              </ul>
            </div>
          </div>
          <!-- /pagination -->
        </div>
      </div>
    </div>
  </div>
  <!-- Delete Modal -->
  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center custom-modal-body">
          <span class="avatar avatar-lg bg-danger-transparent rounded-circle mb-2">
            <i class="isax isax-trash fs-24 text-danger"></i>
          </span>
          <div>
            <h4 class="mb-2">Delete Course</h4>
            <p class="mb-3">Are you sure you want to delete course?</p>
            <div class="d-flex align-items-center justify-content-center">
              <a href="javascript:void(0);" class="btn bg-gray-100 rounded-pill me-2" data-bs-dismiss="modal">Cancel</a>
              <a href="javascript:void(0);" class="btn btn-secondary rounded-pill">Yes, Delete</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
  <CourseFilter :hide-instructor="true" />
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>
